import { useEffect, type RefObject } from 'react'

/**
 * Modal icin odak tuzagi - PAZARLIKSIZ erisilebilirlik gereksinimleri.
 *
 * Bir modal acikken:
 *   * odak modal'a TASINIYOR (aksi halde ekran okuyucu kullanicisi hala
 *     arkadaki sayfada dolasiyor ve modal'in varligini bilmiyor),
 *   * `Tab` modal'in ICINDE donuyor (arkadaki forma sekmelemek mumkun
 *     olmamali - gorunmeyen bir alana yazi yazan bir kullanici uretir),
 *   * kapanista odak ACAN ogeye GERI DONUYOR (yoksa odak `<body>`ye duser ve
 *     klavye kullanicisi yerini kaybeder),
 *   * `Esc` kapatiyor,
 *   * `<body>` scroll'u kilitleniyor.
 *
 * Bunlarin tamami tek bir hook'ta: modal basina yeniden yazilirsa biri
 * mutlaka atlanir.
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Oge odaklanabilir mi?
 *
 * `offsetParent !== null` KULLANMIYORUZ. Iki sebep:
 *   1. Duzen hesabina bagli - jsdom'da her zaman `null` donuyor ve test
 *      ortaminda tuzak calismiyor gibi gorunuyor (yani hatayi TESTLE
 *      yakalayamiyorsun).
 *   2. `position: fixed` bir ogede de `null` donuyor - ve bu modal tam
 *      olarak fixed bir backdrop icinde yasiyor.
 *
 * Onun yerine ACIKCA gizlenmis ogeler dislaniyor. Kapsam bilincli olarak
 * dar: `display: none` bir alt agac zaten `querySelectorAll`da gorunuyor ama
 * `hidden`/`aria-hidden` isaretlenmemis gizli bir oge nadir ve tuzagi
 * duzen hesabina baglamaya degmiyor.
 */
function isVisible(element: HTMLElement): boolean {
  if (element.hidden) return false
  if (element.closest('[hidden]')) return false
  if (element.closest('[aria-hidden="true"]')) return false
  return true
}

export function useFocusTrap(
  container: RefObject<HTMLElement | null>,
  options: { active: boolean; onClose(): void },
): void {
  const { active, onClose } = options

  useEffect(() => {
    if (!active) return
    const node = container.current
    if (!node) return

    // Kapanista buraya donulecek.
    const previous = document.activeElement as HTMLElement | null

    // Ilk odaklanabilir ogeye tasi; hicbiri yoksa kabin kendisine
    // (`tabIndex={-1}` ile odaklanabilir yapiliyor).
    const first = node.querySelector<HTMLElement>(FOCUSABLE)
    ;(first ?? node).focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab') return

      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isVisible)
      if (items.length === 0) {
        event.preventDefault()
        return
      }

      const firstItem = items[0]
      const lastItem = items[items.length - 1]
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault()
        lastItem.focus()
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault()
        firstItem.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)

    // Scroll kilidi. Pozisyon korunuyor: iOS'ta `overflow: hidden` sayfayi
    // basa atiyor ve modal kapaninca kullanici yerini kaybediyor.
    const scrollY = window.scrollY
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = previousOverflow
      window.scrollTo(0, scrollY)
      previous?.focus?.()
    }
  }, [active, container, onClose])
}
